<template>
  <div class="modal-overlay" @click.self="$emit('close')" role="dialog" aria-modal="true" aria-labelledby="subscription-modal-heading">
    <div class="modal-content" :class="{ 'modal-content-compact': !showPlans, 'modal-content-plans': showPlans }">
      <div class="modal-header">
        <div class="header-content">
          <div class="icon-wrapper" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h10v2H4v-2z" fill="currentColor"/>
            </svg>
          </div>
          <div>
            <h2 id="subscription-modal-heading">{{ isSubUser ? 'Subscription access' : 'Manage subscription' }}</h2>
            <p>{{ isSubUser ? 'Account requires admin action' : 'Your current plan and billing details' }}</p>
          </div>
        </div>
        <button type="button" class="close-btn" @click="$emit('close')" aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>

      <div class="body-wrap">
        <div v-if="loading" class="state-block" role="status" aria-live="polite">
          <div class="loader-card">
            <div class="spinner" aria-hidden="true" />
            <p>Loading subscription details...</p>
            <small>Please wait while we fetch your latest plan status.</small>
          </div>
        </div>

        <template v-else-if="isSubUser">
          <div class="subuser-message-card" role="status" aria-live="polite">
            <div class="subuser-message-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 9v4m0 4h.01M4.93 19h14.14c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.2 16c-.77 1.33.19 3 1.73 3z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h3>Plan expired. Contact admin to reactivate this account.</h3>
            <p>
              Subuser accounts cannot purchase plans directly. Please ask your primary admin to renew or upgrade the subscription.
            </p>
            <button type="button" class="btn-secondary" @click="$emit('close')">Close</button>
          </div>
        </template>

        <template v-else-if="showPlans">
          <div class="plans-header">
            <p class="plans-subtitle">
              Choose this plan to unlock all dashboard modules. Every plan includes full feature access; only property capacity changes.
            </p>
          </div>

          <div v-if="plans.length" class="plans-grid">
            <article
              v-for="plan in plans"
              :key="plan.planId"
              class="plan-card"
              :class="{ 'is-highlighted': normalized?.planId && normalized.planId === plan.planId }"
            >
              <div class="plan-card-top">
                <div>
                  <h4 class="plan-card-name">{{ plan.planname || 'Plan' }}</h4>
                  <p class="plan-card-desc">{{ plan.description || 'Subscription plan' }}</p>
                </div>
                <span class="plan-card-status">{{ formatStatus(plan.planstatus || 'ACTIVE') }}</span>
              </div>

              <div class="plan-card-price-row">
                <span class="plan-card-price">{{ formatMoney(plan.amount, plan.currency) }}</span>
                <span class="plan-card-interval">{{ mapInterval(plan.planinterval) }}</span>
              </div>

              <div class="plan-card-meta">
                <span class="meta-chip">{{ plan.objectcapacity || 0 }} {{ Number(plan.objectcapacity || 0) === 1 ? 'property' : 'properties' }}</span>
                <span class="meta-chip" v-if="Number(plan.trialperioddays || 0) > 0">{{ plan.trialperioddays }} day trial</span>
              </div>

              <div class="plan-feature-wrap">
                <ul class="feature-pill-list" aria-label="Included features">
                  <li v-for="feature in includedFeatures" :key="`${plan.planId}-${feature}`" class="feature-pill">{{ feature }}</li>
                </ul>
              </div>

              <button
                type="button"
                class="btn-primary subscribe-btn"
                @click="subscribeToPlan(plan)"
              >
                Subscribe
              </button>
            </article>
          </div>

          <div v-else class="state-block state-error">
            <p>No plans are available at the moment.</p>
          </div>
        </template>

        <div v-else-if="errorMessage" class="state-block state-error">
          <p>{{ errorMessage }}</p>
          <button type="button" class="btn-secondary" @click="fetchSubscription">Try again</button>
        </div>

        <template v-else-if="normalized">
          <div class="plan-hero">
            <div class="plan-title-row">
              <h3 class="plan-name">{{ normalized.planName || '—' }}</h3>
              <span class="status-pill" :class="statusClass">{{ formatStatus(normalized.planStatus) }}</span>
            </div>
            <div class="price-row">
              <span class="price">{{ formatMoney(normalized.amount, normalized.currency) }}</span>
              <span v-if="intervalLabel" class="interval">{{ intervalLabel }}</span>
            </div>
          </div>

          <ul class="detail-grid" aria-label="Subscription details">
            <li v-if="normalized.planId">
              <span class="detail-label">Plan ID</span>
              <span class="detail-value mono">{{ normalized.planId }}</span>
            </li>
            <li v-if="normalized.objectCapacity != null">
              <span class="detail-label">Property capacity</span>
              <span class="detail-value">{{ normalized.objectCapacity }} {{ normalized.objectCapacity === 1 ? 'property' : 'properties' }}</span>
            </li>
            <li v-if="normalized.trialPeriodDays > 0">
              <span class="detail-label">Trial</span>
              <span class="detail-value">{{ normalized.trialPeriodDays }} days included</span>
            </li>
            <li v-if="normalized.planStartDate">
              <span class="detail-label">Plan started</span>
              <span class="detail-value">{{ formatDateTime(normalized.planStartDate) }}</span>
            </li>
            <li v-if="normalized.nextBilling">
              <span class="detail-label">Expire at</span>
              <span class="detail-value">{{ formatDateTime(normalized.nextBilling) }}</span>
            </li>
          </ul>

          <div class="footer-actions">
            <button type="button" class="btn-secondary" @click="$emit('close')">Close</button>
            <button type="button" class="btn-primary" @click="fetchSubscription">Refresh</button>
          </div>
        </template>
      </div>
    </div>

    <div v-if="showGatewayPicker" class="gateway-overlay" @click.self="closeGatewayPicker">
      <div class="gateway-card" role="dialog" aria-modal="true" aria-labelledby="gateway-title">
        <h3 id="gateway-title">Choose payment gateway</h3>
        <div v-if="gatewayLoading" class="loader-card loader-card-compact gateway-state">
          <div class="spinner spinner-sm" aria-hidden="true" />
          <p>Loading available gateways...</p>
        </div>
        <div v-else-if="isOpeningGateway" class="loader-card loader-card-compact gateway-state">
          <div class="spinner spinner-sm" aria-hidden="true" />
          <p>Opening payment gateway securely...</p>
        </div>
        <p v-else-if="gatewayError" class="gateway-state gateway-error">{{ gatewayError }}</p>
        <ul v-else class="gateway-list">
          <li v-for="gateway in gateways" :key="gateway.idgatewaycfg">
            <button
              type="button"
              class="gateway-btn"
              :disabled="isOpeningGateway"
              @click="selectGateway(gateway)"
            >
              <span class="gateway-btn-main">
                <span class="gateway-logo-wrap">
                  <img
                    v-if="gatewayLogo(gateway.idgatewaycfg)"
                    :src="gatewayLogo(gateway.idgatewaycfg)"
                    :alt="`${gateway.idgatewaycfg} logo`"
                    class="gateway-logo"
                  />
                </span>
                <span class="gateway-meta">
                  <strong>{{ gateway.idgatewaycfg }}</strong>
                  <small>{{ gateway.idgatewaycfg === 'ESEWA' ? 'Available now' : 'Coming soon' }}</small>
                </span>
              </span>
              <span class="gateway-chevron">›</span>
            </button>
          </li>
        </ul>
        <div class="gateway-actions">
          <button type="button" class="btn-secondary" :disabled="isOpeningGateway" @click="closeGatewayPicker">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '@/utilities/errorHelper';
import { startEsewaSubscriptionCheckout } from '@/utilities/esewaPayment';
import esewaLogo from '@/assets/esewa.png';
import khaltiLogo from '@/assets/khalti.png';

const SUBSCRIPTION_STORAGE_KEY = 'SUBSCRIPTION_DATA';
const GATEWAY_LOGO_MAP = {
  ESEWA: esewaLogo,
  KHALTI: khaltiLogo,
};

export default {
  name: 'ManageSubscription',
  props: {
    adminProfileData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: true,
      errorMessage: '',
      raw: null,
      plans: [],
      showPlans: false,
      showGatewayPicker: false,
      gatewayLoading: false,
      gatewayError: '',
      gateways: [],
      pendingPlan: null,
      isOpeningGateway: false,
      subscribeUrl:
        typeof process !== 'undefined' && process.env?.VUE_APP_SUBSCRIBE_URL
          ? String(process.env.VUE_APP_SUBSCRIBE_URL).trim()
          : '',
      includedFeatures: [
        'Dashboard',
        'Rooms',
        'Guests',
        'New Bookings',
        'Rent Payment',
        'Maintenance',
        'Outing Attendance',
        'Media Uploads',
        'Food and Menu',
        'Staff Management',
      ],
    };
  },
  computed: {
    isSubUser() {
      if (this.adminProfileData?.userType) {
        return String(this.adminProfileData.userType).toUpperCase() === 'SUBUSER';
      }
      try {
        const stored = localStorage.getItem('ADMIN_PROFILE_DATA');
        const parsed = stored ? JSON.parse(stored) : null;
        return String(parsed?.userType || '').toUpperCase() === 'SUBUSER';
      } catch {
        return false;
      }
    },
    normalized() {
      const d = this.raw;
      if (!d || typeof d !== 'object') {
        return null;
      }
      return {
        planId: d.planId ?? d.planid,
        planName: d.planname ?? d.planName,
        description: d.description,
        amount: d.amount,
        currency: d.currency,
        planInterval: d.planinterval ?? d.planInterval,
        objectCapacity: d.objectcapacity ?? d.objectCapacity,
        planStatus: d.planstatus ?? d.planStatus,
        trialPeriodDays: Number(d.trialperioddays ?? d.trialPeriodDays ?? 0),
        planStartDate: d.planstartdate ?? d.planStartDate,
        nextBilling: d.nextbilling ?? d.nextBilling,
      };
    },
    intervalLabel() {
      const i = (this.normalized?.planInterval || '').toString().toUpperCase();
      if (i === 'YEAR' || i === 'YEARLY') return 'per year';
      if (i === 'MONTH' || i === 'MONTHLY') return 'per month';
      if (i === 'DAY' || i === 'DAILY') return 'per day';
      return i ? i.toLowerCase().replace(/_/g, ' ') : '';
    },
    statusClass() {
      const s = (this.normalized?.planStatus || '').toString().toUpperCase();
      if (s === 'ACTIVE') return 'status-active';
      if (s === 'TRIAL' || s === 'TRIALING') return 'status-trial';
      return 'status-neutral';
    },
  },
  mounted() {
    if (this.isSubUser) {
      this.loading = false;
      return;
    }
    if (this.loadCachedSubscription()) {
      this.loading = false;
      return;
    }
    this.fetchSubscription();
  },
  methods: {
    loadCachedSubscription() {
      try {
        const cached = localStorage.getItem(SUBSCRIPTION_STORAGE_KEY);
        if (!cached) {
          return false;
        }
        const parsed = JSON.parse(cached);
        if (!parsed || typeof parsed !== 'object') {
          return false;
        }
        this.raw = parsed;
        this.errorMessage = '';
        this.showPlans = false;
        return true;
      } catch (error) {
        console.warn('Failed to read cached subscription:', error);
        return false;
      }
    },
    saveCachedSubscription(data) {
      if (!data || typeof data !== 'object') {
        return;
      }
      try {
        localStorage.setItem(SUBSCRIPTION_STORAGE_KEY, JSON.stringify(data));
      } catch (error) {
        console.warn('Failed to cache subscription:', error);
      }
    },
    formatStatus(status) {
      if (!status) return '—';
      return String(status).replace(/_/g, ' ');
    },
    mapInterval(interval) {
      const i = String(interval || '').toUpperCase();
      if (i === 'YEAR' || i === 'YEARLY') return 'per year';
      if (i === 'MONTH' || i === 'MONTHLY') return 'per month';
      if (i === 'DAY' || i === 'DAILY') return 'per day';
      return i ? i.toLowerCase().replace(/_/g, ' ') : '';
    },
    /** SMS amounts are returned in minor units (e.g. paisa); convert for display. */
    formatMoney(amount, currency = 'NPR') {
      if (amount == null || amount === '') return '—';
      const n = Number(amount);
      if (Number.isNaN(n)) return '—';
      const major = n / 100;
      try {
        return new Intl.NumberFormat('en-NP', {
          style: 'currency',
          currency: currency || 'NPR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(major);
      } catch {
        return `${currency || 'NPR'} ${major.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
      }
    },
    formatDateTime(value) {
      if (!value) return '—';
      const d = new Date(String(value).replace(' ', 'T'));
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    },
    extractErrorMessage(error) {
      const data = error?.response?.data;
      if (data?.reason) return data.reason;
      if (data?.message) return data.message;
      if (error?.message) return error.message;
      return 'Could not load subscription.';
    },
    isNoActiveSubscriptionError(error) {
      const errorCode = Number(error?.response?.data?.errorcode);
      return errorCode === 3231;
    },
    async fetchAvailablePlans() {
      try {
        const response = await apiService.getVisiblePlans();
        const list = response?.data?.data;
        this.plans = Array.isArray(list) ? list : [];
      } catch (planError) {
        console.error('getVisiblePlans failed:', planError);
        this.plans = [];
      }
    },
    async openGatewayPicker(plan) {
      this.pendingPlan = plan;
      this.showGatewayPicker = true;
      this.gatewayLoading = true;
      this.gatewayError = '';
      this.gateways = [];
      try {
        const response = await apiService.getPaymentGateways();
        const list = Array.isArray(response?.data) ? response.data : [];
        this.gateways = list.filter((g) => String(g?.gatewaystatus || '').toUpperCase() === 'ACTIVE');
        if (!this.gateways.length) {
          this.gatewayError = 'No active payment gateway available right now.';
        }
      } catch (error) {
        console.error('Failed to fetch payment gateways:', error);
        this.gatewayError = error?.response?.data?.reason || 'Failed to load payment gateways.';
      } finally {
        this.gatewayLoading = false;
      }
    },
    closeGatewayPicker() {
      if (this.isOpeningGateway) {
        return;
      }
      this.showGatewayPicker = false;
      this.gatewayLoading = false;
      this.gatewayError = '';
      this.gateways = [];
      this.pendingPlan = null;
    },
    selectGateway(gateway) {
      const gatewayCode = String(gateway?.idgatewaycfg || '').toUpperCase();
      if (gatewayCode !== 'ESEWA') {
        this.gatewayError = `${gatewayCode || 'This'} gateway is coming soon.`;
        return;
      }
      this.isOpeningGateway = true;
      this.errorMessage = '';
      startEsewaSubscriptionCheckout(this.pendingPlan).catch((error) => {
        console.error('Failed to initiate eSewa payment:', error);
        this.gatewayError = error?.response?.data?.reason || error?.message || 'Could not start payment.';
        this.isOpeningGateway = false;
      });
    },
    gatewayLogo(code) {
      const key = String(code || '').toUpperCase();
      return GATEWAY_LOGO_MAP[key] || '';
    },
    subscribeToPlan(plan) {
      this.errorMessage = '';
      this.openGatewayPicker(plan);
    },
    async fetchSubscription() {
      this.loading = true;
      this.errorMessage = '';
      this.showPlans = false;
      this.plans = [];
      try {
        const { data } = await apiService.getSubscription();
        this.raw = data && typeof data === 'object' ? data : null;
        if (!this.raw) {
          this.errorMessage = 'No subscription data returned.';
        } else {
          this.saveCachedSubscription(this.raw);
        }
      } catch (error) {
        if (this.isNoActiveSubscriptionError(error)) {
          this.raw = null;
          this.errorMessage = '';
          this.showPlans = true;
          await this.fetchAvailablePlans();
          return;
        }
        if (!isNoDataError(error) && !isHmsSubscriptionInactiveError(error)) {
          console.error('getSubscription failed:', error);
        }
        this.errorMessage = this.extractErrorMessage(error);
        this.raw = null;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 80px 16px 32px;
  z-index: 9999;
  overflow-y: auto;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background: #ffffff;
  border-radius: 20px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  box-shadow:
    0 24px 60px -18px rgba(31, 41, 55, 0.35),
    0 2px 8px rgba(91, 46, 142, 0.12);
  animation: slideUp 0.3s ease-out;
  display: grid;
  grid-template-rows: auto 1fr;
}

.modal-content-compact {
  max-width: 560px;
}

.modal-content-plans {
  max-width: 980px;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
  color: white;
  padding: 24px 32px;
  position: relative;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-right: 48px;
}

.icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-header h2 {
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
}

.modal-header p {
  margin: 0;
  font-size: 14px;
  opacity: 0.92;
  font-family: 'Poppins', sans-serif;
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.body-wrap {
  padding: 24px 28px 40px;
  overflow-y: auto;
  max-height: calc(90vh - 140px);
  font-family: 'Poppins', sans-serif;
  background: linear-gradient(180deg, #ffffff 0%, #fafafd 100%);
}

.state-block {
  text-align: center;
  padding: 1.2rem 0.6rem;
  color: #6b7280;
  font-size: 15px;
}

.loader-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.55rem;
  padding: 1rem 1rem 0.95rem;
  border-radius: 12px;
  background: linear-gradient(150deg, #ffffff 0%, #f7f4ff 100%);
  border: 1px solid rgba(91, 46, 142, 0.14);
  box-shadow: 0 8px 20px rgba(91, 46, 142, 0.08);
}

.loader-card p {
  margin: 0;
  color: #4b5563;
  font-size: 0.9rem;
  font-weight: 600;
}

.loader-card small {
  margin: 0;
  color: #6b7280;
  font-size: 0.78rem;
  line-height: 1.4;
}

.loader-card-compact {
  padding: 0.85rem 0.9rem;
}

.state-error {
  color: #374151;
}

.state-error p {
  margin: 0 0 1rem;
}

.subuser-message-card {
  padding: 20px 18px;
  border-radius: 14px;
  border: 1px solid rgba(245, 158, 11, 0.28);
  background: linear-gradient(165deg, #fffbeb 0%, #ffffff 100%);
  box-shadow: 0 8px 24px rgba(245, 158, 11, 0.12);
  text-align: left;
}

.subuser-message-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #b45309;
  background: rgba(245, 158, 11, 0.16);
  margin-bottom: 10px;
}

.subuser-message-card h3 {
  margin: 0 0 8px;
  font-size: 1.05rem;
  font-weight: 700;
  color: #92400e;
}

.subuser-message-card p {
  margin: 0 0 14px;
  font-size: 0.9rem;
  line-height: 1.55;
  color: #78350f;
}

.plans-header {
  text-align: left;
  margin-bottom: 1.1rem;
  padding: 12px 14px;
  border-radius: 14px;
  background: linear-gradient(120deg, rgba(91, 46, 142, 0.08), rgba(124, 58, 237, 0.04));
  border: 1px solid rgba(91, 46, 142, 0.12);
}

.plans-title {
  margin: 0 0 6px;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1f2937;
}

.plans-subtitle {
  margin: 0;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.55;
}

.feature-pill-list {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0;
  margin: 0;
}

.feature-pill {
  font-size: 11px;
  font-weight: 600;
  color: #5b2e8e;
  background: rgba(91, 46, 142, 0.08);
  border: 1px solid rgba(91, 46, 142, 0.15);
  border-radius: 999px;
  padding: 5px 10px;
}

.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 10px;
}

.plan-card {
  border: 1px solid rgba(91, 46, 142, 0.16);
  border-radius: 16px;
  background: linear-gradient(170deg, #ffffff 0%, #faf8ff 55%, #f8f4ff 100%);
  padding: 18px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 13px;
  box-shadow: 0 10px 28px rgba(91, 46, 142, 0.1);
  min-height: 100%;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.plan-card:hover {
  transform: translateY(-2px);
  border-color: rgba(91, 46, 142, 0.28);
  box-shadow: 0 14px 34px rgba(91, 46, 142, 0.14);
}

.plan-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.plan-card-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
}

.plan-card-desc {
  margin: 4px 0 0;
  font-size: 12px;
  color: #6b7280;
  min-height: 34px;
}

.plan-card-status {
  background: rgba(34, 197, 94, 0.15);
  color: #15803d;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 4px 8px;
}

.plan-card-price-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.plan-card-price {
  font-size: 1.5rem;
  font-weight: 700;
  color: #5b2e8e;
}

.plan-card-interval {
  color: #6b7280;
  font-size: 12px;
}

.plan-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 30px;
}

.meta-chip {
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  background: #f3f4f6;
  border-radius: 999px;
  padding: 4px 8px;
}

.plan-feature-wrap {
  border-top: 1px solid rgba(91, 46, 142, 0.1);
  padding-top: 12px;
}

.subscribe-btn {
  width: 100%;
  margin-top: auto;
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0;
  border-radius: 50%;
  border: 3px solid rgba(91, 46, 142, 0.16);
  border-top-color: #5b2e8e;
  border-right-color: #7c3aed;
  animation: spin 0.85s linear infinite;
}

.spinner-sm {
  width: 28px;
  height: 28px;
  border-width: 2.5px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.plan-hero {
  margin-bottom: 0.85rem;
  padding: 14px 16px;
  border-radius: 16px;
  background: linear-gradient(155deg, #ffffff 0%, #f7f2ff 100%);
  border: 1px solid rgba(91, 46, 142, 0.12);
  box-shadow: 0 8px 22px rgba(91, 46, 142, 0.08);
}

.plan-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.plan-name {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: -0.02em;
}

.plan-desc {
  margin: 0 0 1rem;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
}

.status-pill {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 3px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
}

.status-active {
  background: rgba(34, 197, 94, 0.15);
  color: #15803d;
}

.status-trial {
  background: rgba(59, 130, 246, 0.12);
  color: #1d4ed8;
}

.price-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
}

.price {
  font-size: 1.45rem;
  font-weight: 700;
  color: #5b2e8e;
  letter-spacing: -0.02em;
}

.interval {
  font-size: 12px;
  color: #6b7280;
}

.detail-grid {
  list-style: none;
  margin: 0;
  padding: 10px 12px;
  display: grid;
  gap: 6px;
  border: 1px solid rgba(91, 46, 142, 0.1);
  border-radius: 14px;
  background: #fff;
}

.detail-grid li {
  display: grid;
  grid-template-columns: minmax(120px, 155px) 1fr;
  align-items: start;
  gap: 8px 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(91, 46, 142, 0.06);
}

.detail-grid li:last-child {
  border-bottom: none;
}

.detail-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
  line-height: 1.4;
  padding-top: 2px;
}

.detail-value {
  font-size: 14px;
  color: #1f2937;
  font-weight: 600;
  line-height: 1.35;
  text-align: left;
}

.mono {
  font-family: ui-monospace, monospace;
  font-size: 13px;
  word-break: break-all;
}

.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 0.85rem;
  padding-top: 12px;
  border-top: 1px solid rgba(91, 46, 142, 0.12);
  flex-wrap: wrap;
}

@media (max-width: 640px) {
  .modal-content {
    max-width: 100%;
  }

  .body-wrap {
    padding: 18px 14px 18px;
  }

  .plans-grid {
    grid-template-columns: 1fr;
  }

  .detail-grid li {
    grid-template-columns: 1fr;
    gap: 4px;
    padding: 12px 0;
  }

  .detail-label {
    font-size: 11px;
  }

  .detail-value {
    font-size: 14px;
  }
}

.btn-secondary,
.btn-primary {
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 10px;
  cursor: pointer;
  border: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.btn-primary {
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  color: white;
  box-shadow: 0 4px 14px rgba(91, 46, 142, 0.25);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(91, 46, 142, 0.3);
}

.gateway-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10020;
  padding: 16px;
}

.gateway-card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.24);
}

.gateway-card h3 {
  margin: 0 0 10px;
  font-size: 1.05rem;
  color: #1f2937;
}

.gateway-state {
  margin: 0;
  color: #6b7280;
  font-size: 0.9rem;
}

.gateway-error {
  color: #b91c1c;
}

.gateway-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 12px;
}

.gateway-btn {
  width: 100%;
  border: 1px solid rgba(91, 46, 142, 0.18);
  background: #fff;
  border-radius: 12px;
  padding: 11px 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-weight: 500;
  color: #1f2937;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.12s ease;
}

.gateway-btn:hover {
  border-color: rgba(91, 46, 142, 0.34);
  box-shadow: 0 6px 15px rgba(91, 46, 142, 0.12);
  transform: translateY(-1px);
}

.gateway-btn-main {
  display: inline-flex;
  align-items: center;
  gap: 0.9rem;
}

.gateway-logo-wrap {
  width: 52px;
  height: 52px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.24);
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.gateway-logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.gateway-meta {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
}

.gateway-meta strong {
  font-size: 0.98rem;
  font-weight: 700;
  color: #111827;
}

.gateway-btn small {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.gateway-chevron {
  color: #64748b;
  font-size: 1.25rem;
  font-weight: 700;
}

.gateway-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}
</style>
